// Live media from /api/devices/:id/media, decoded with WebCodecs:
// H.264 Annex-B video (camera or live screen) onto a canvas, Opus call audio
// to the speakers. The same socket carries remote-control JSON both ways.

import {
  MEDIA_HEADER_BYTES,
  MEDIA_KIND_AUDIO,
  MEDIA_KIND_VIDEO,
  MEDIA_SOURCE_CAMERA,
  type RemoteEvent,
  type RemoteInput,
} from "@fistos/shared/api";

export function webCodecsSupported(): boolean {
  return typeof VideoDecoder !== "undefined" && typeof AudioDecoder !== "undefined";
}

/** The first SPS NAL unit (without start code) of an Annex-B access unit. */
function findSps(au: Uint8Array): Uint8Array | null {
  for (let i = 0; i + 4 < au.length; i++) {
    const three = au[i] === 0 && au[i + 1] === 0 && au[i + 2] === 1;
    if (!three) continue;
    const nal = i + 3;
    if ((au[nal] & 0x1f) !== 7 || nal + 3 >= au.length) continue;
    let end = nal + 1;
    while (end + 2 < au.length && !(au[end] === 0 && au[end + 1] === 0 && (au[end + 2] === 1 || au[end + 2] === 0)))
      end++;
    return au.subarray(nal, end + 2 < au.length ? end : au.length);
  }
  return null;
}

/** avc1.PPCCLL from an SPS NAL unit. */
function codecFromSps(sps: Uint8Array): string {
  const hex = (b: number) => b.toString(16).padStart(2, "0");
  return `avc1.${hex(sps[1])}${hex(sps[2])}${hex(sps[3])}`;
}

const sameBytes = (a: Uint8Array | null, b: Uint8Array) => a?.length === b.length && a.every((v, i) => v === b[i]);

export interface MediaStats {
  width: number;
  height: number;
  frames: number;
  audioPackets: number;
}

export class MediaSession {
  private ws: WebSocket | null = null;
  private video: VideoDecoder | null = null;
  private audio: AudioDecoder | null = null;
  private ctx: AudioContext | null = null;
  private playAt = 0;
  private waitingKey = true;
  private sps: Uint8Array | null = null;
  stats: MediaStats = { width: 0, height: 0, frames: 0, audioPackets: 0 };

  constructor(
    private readonly deviceId: string,
    private readonly opts: {
      canvas?: HTMLCanvasElement;
      /** Which video to draw (MEDIA_SOURCE_*); default camera. */
      source?: number;
      audio?: boolean;
      onOpen?: () => void;
      onRemote?: (e: RemoteEvent) => void;
      onStats?: (s: MediaStats) => void;
      onError?: (m: string) => void;
    },
  ) {}

  start(): void {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/api/devices/${this.deviceId}/media`);
    ws.binaryType = "arraybuffer";
    ws.onmessage = (m) => {
      if (typeof m.data === "string") {
        try {
          this.opts.onRemote?.(JSON.parse(m.data) as RemoteEvent);
        } catch {
          // not ours
        }
        return;
      }
      this.onFrame(new Uint8Array(m.data as ArrayBuffer));
    };
    ws.onopen = () => this.opts.onOpen?.();
    ws.onclose = (e) => {
      if (e.code !== 1000 && this.ws) this.opts.onError?.("Media connection closed");
    };
    this.ws = ws;
    if (this.opts.audio) {
      this.ctx = new AudioContext({ sampleRate: 48000 });
      this.audio = new AudioDecoder({
        output: (data) => this.playAudio(data),
        error: (e) => this.opts.onError?.(`Audio decoder: ${e.message}`),
      });
      this.audio.configure({ codec: "opus", sampleRate: 48000, numberOfChannels: 1 });
    }
  }

  /** Sends a remote-control message; false if the socket is not open. */
  send(m: RemoteInput): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(m));
    return true;
  }

  /** Bytes queued on the socket (input backpressure). */
  get buffered(): number {
    return this.ws?.bufferedAmount ?? 0;
  }

  stop(): void {
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000);
    if (this.video && this.video.state !== "closed") this.video.close();
    if (this.audio && this.audio.state !== "closed") this.audio.close();
    void this.ctx?.close();
    this.video = this.audio = null;
    this.ctx = null;
  }

  private onFrame(buf: Uint8Array): void {
    if (buf.length < MEDIA_HEADER_BYTES) return;
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    const kind = buf[0];
    const key = (buf[1] & 1) === 1;
    const width = view.getUint16(2, true);
    const height = view.getUint16(4, true);
    const source = buf[6];
    const pts = Number(view.getBigUint64(8, true));
    const payload = buf.subarray(MEDIA_HEADER_BYTES);
    if (kind === MEDIA_KIND_VIDEO) {
      if (source === (this.opts.source ?? MEDIA_SOURCE_CAMERA)) this.onVideo(payload, key, pts, width, height);
    } else if (kind === MEDIA_KIND_AUDIO && this.audio?.state === "configured") {
      this.audio.decode(new EncodedAudioChunk({ type: "key", timestamp: pts, data: payload }));
      this.stats.audioPackets++;
    }
  }

  private onVideo(au: Uint8Array, key: boolean, pts: number, width: number, height: number): void {
    const canvas = this.opts.canvas;
    if (!canvas) return;
    // A keyframe with a different SPS (the screen rotated, or the stream
    // restarted at another size) gets a fresh decoder.
    const sps = key ? findSps(au) : null;
    if (sps && !this.waitingKey && !sameBytes(this.sps, sps)) {
      if (this.video && this.video.state !== "closed") this.video.close();
      this.video = null;
      this.waitingKey = true;
    }
    if (this.waitingKey) {
      if (!key || !sps) return;
      this.sps = sps.slice();
      const codec = codecFromSps(sps);
      this.video = new VideoDecoder({
        output: (frame) => {
          if (canvas.width !== frame.displayWidth) canvas.width = frame.displayWidth;
          if (canvas.height !== frame.displayHeight) canvas.height = frame.displayHeight;
          canvas.getContext("2d")?.drawImage(frame, 0, 0);
          this.stats = {
            ...this.stats,
            width: frame.displayWidth,
            height: frame.displayHeight,
            frames: this.stats.frames + 1,
          };
          this.opts.onStats?.(this.stats);
          frame.close();
        },
        error: (e) => {
          this.opts.onError?.(`Video decoder: ${e.message}`);
          this.waitingKey = true;
        },
      });
      // No `description`: chunks are Annex-B with in-band SPS/PPS.
      this.video.configure({
        codec,
        optimizeForLatency: true,
        ...(width && height ? { codedWidth: width, codedHeight: height } : {}),
      });
      this.waitingKey = false;
    }
    if (this.video?.state !== "configured") return;
    if (this.video.decodeQueueSize > 30 && !key) return; // fall behind gracefully
    this.video.decode(new EncodedVideoChunk({ type: key ? "key" : "delta", timestamp: pts, data: au }));
  }

  private playAudio(data: AudioData): void {
    const ctx = this.ctx;
    if (!ctx) return data.close();
    const buf = ctx.createBuffer(1, data.numberOfFrames, data.sampleRate);
    data.copyTo(buf.getChannelData(0), { planeIndex: 0, format: "f32-planar" });
    data.close();
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    // Small jitter buffer: start 100 ms ahead, resync if we fell behind.
    const now = ctx.currentTime;
    if (this.playAt < now || this.playAt > now + 1) this.playAt = now + 0.1;
    src.start(this.playAt);
    this.playAt += buf.duration;
  }
}
